<template>
  <div class="col-md-6 mb-3">
    <ControllerInfoCard
      :controller="controllerWithLastVersion"
      :title="controllerTypeText"
      :room-names="roomDisplayNames"
      badge-mode="auto"
      variant="withRooms"
    >
      <template #headerLeftExtra>
        <div class="form-check" v-if="props.isSelect">
          <input
            class="form-check-input check-controller"
            type="checkbox"
            :value="props.data._id"
            @change="onCheckboxChange"
          />
        </div>
      </template>

      <template #headerRightExtra>
        <div v-if="shouldShowDelete" @click.stop="onDeleteClick" class="controller-delete-btn">
          <i class="fa-regular fa-trash-can"></i>
        </div>
      </template>

      <template #bodyExtra>
        <div class="controller-info-item" v-if="props.isSelect">
          <div class="controller-info-line">
            <i class="fa-solid fa-wifi me-2"></i>
            <span class="controller-info-label">WiFi SSID:</span>
            <span class="controller-info-value">
              <span v-if="hasSsid">{{ props.data.ssid }}</span>
              <span v-else class="controller-info-no-data">정보 없음</span>
            </span>
          </div>
        </div>
        <div class="controller-info-item sensor-link-section" v-if="props.showAoLink">
          <div class="controller-info-line ao-link-title">
            <i class="fa-solid fa-satellite-dish me-2"></i>
            <span class="controller-info-label">센서 연동</span>
          </div>
          <div class="sensor-link-grid">
            <div
              v-for="ch in sensorChannels"
              :key="`sensor-ch-${ch}`"
              class="form-check sensor-link-check"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="`sensor-link-${props.data._id}-${ch}`"
                :checked="isSensorLinked(ch)"
                :disabled="sensorLinkSaving"
                @change="onSensorLinkChange(ch, $event.target.checked)"
              />
              <label
                class="form-check-label ao-link-label"
                :for="`sensor-link-${props.data._id}-${ch}`"
              >
                {{ ch }}번
              </label>
            </div>
          </div>
          <div
            class="ao-link-title night-sensor-link-title"
            :class="{ disabled: !isSensorNightEnable }"
          >
            <span class="controller-info-label">야간 센서 연동</span>
          </div>
          <div
            class="sensor-link-grid"
            :class="{ disabled: !isSensorNightEnable }"
          >
            <div
              v-for="ch in sensorChannels"
              :key="`sensor-night-ch-${ch}`"
              class="form-check sensor-link-check"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="`sensor-night-link-${props.data._id}-${ch}`"
                :checked="isSensorNightLinked(ch)"
                :disabled="sensorNightLinkSaving || !isSensorNightEnable"
                @change="onSensorNightLinkChange(ch, $event.target.checked)"
              />
              <label
                class="form-check-label ao-link-label"
                :for="`sensor-night-link-${props.data._id}-${ch}`"
              >
                {{ ch }}번
              </label>
            </div>
          </div>
          <div
            class="sensor-off-timer-row"
            @click.stop="onSensorNightRowClick"
          >
            <span class="ao-link-label">야간 센서등</span>
            <span class="sensor-off-timer-value">{{ sensorNightDisplay }}</span>
          </div>
          <div class="sensor-section-divider" aria-hidden="true"></div>
          <div class="sensor-timer-mode-group">
            <div class="form-check sensor-ignore-off-check">
              <input
                class="form-check-input"
                type="radio"
                :name="`sensor-timer-mode-${props.data._id}`"
                :id="`sensor-timer-builtin-${props.data._id}`"
                :checked="!props.data?.sensorIgnoreOff"
                :disabled="sensorIgnoreOffSaving"
                @change="onSensorIgnoreOffChange(false)"
              />
              <label
                class="form-check-label ao-link-label"
                :for="`sensor-timer-builtin-${props.data._id}`"
              >
                센서 내장 타이머
              </label>
            </div>
            <div class="form-check sensor-ignore-off-check">
              <input
                class="form-check-input"
                type="radio"
                :name="`sensor-timer-mode-${props.data._id}`"
                :id="`sensor-timer-user-${props.data._id}`"
                :checked="!!props.data?.sensorIgnoreOff"
                :disabled="sensorIgnoreOffSaving"
                @change="onSensorIgnoreOffChange(true)"
              />
              <label
                class="form-check-label ao-link-label"
                :for="`sensor-timer-user-${props.data._id}`"
              >
                사용자 설정 타이머
              </label>
            </div>
          </div>
          <div
            class="sensor-off-timer-row"
            :class="{ disabled: !props.data?.sensorIgnoreOff }"
            @click.stop="onSensorOffTimerRowClick"
          >
            <span class="ao-link-label">사용자 설정 타이머</span>
            <span class="sensor-off-timer-value">{{ sensorOffTimerDisplay }}</span>
          </div>
          <div class="form-check sensor-ignore-off-check">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`ao-skip-sensor-on-${props.data._id}`"
              :checked="isAoSkipSensorOn"
              :disabled="aoSkipSensorOnSaving"
              @change="onAoSkipSensorOnChange($event.target.checked)"
            />
            <label
              class="form-check-label ao-link-label"
              :for="`ao-skip-sensor-on-${props.data._id}`"
            >
              일괄스위치 - 센서등 켜지 않기
            </label>
          </div>
          <div class="form-check sensor-ignore-off-check">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`sensor-off-guard-${props.data._id}`"
              :checked="isSensorOffGuard"
              :disabled="sensorOffGuardSaving"
              @change="onSensorOffGuardChange($event.target.checked)"
            />
            <label
              class="form-check-label ao-link-label"
              :for="`sensor-off-guard-${props.data._id}`"
            >
              수동 소등 - 5초 센서 지연
            </label>
          </div>
        </div>

        <PopDialog
          title="사용자 설정 타이머"
          :visible="sensorOffTimerModal.visible"
          :okCallback="onSensorOffTimerApply"
          :cancelCallback="sensorOffTimerModal.hide"
          :okText="'적용'"
          :cancelText="'취소'"
          :clickable="false"
        >
          <div class="sensor-off-timer-picker">
            <div class="sensor-off-timer-field">
              <label class="form-label mb-1">분</label>
              <select class="form-select" v-model.number="timerDraftMin">
                <option v-for="m in timerMinOptions" :key="`tm-${m}`" :value="m">
                  {{ m }}
                </option>
              </select>
            </div>
            <div class="sensor-off-timer-field">
              <label class="form-label mb-1">초</label>
              <select class="form-select" v-model.number="timerDraftSec">
                <option v-for="s in timerSecOptions" :key="`ts-${s}`" :value="s">
                  {{ s }}
                </option>
              </select>
            </div>
          </div>
          <p class="sensor-off-timer-hint mb-0">
            초가 0초이고 분도 0이면 타이머 미사용 (계속 켜짐).<br />
            타이머 만료 시에도 내장센서가 감지 중이면, 센서가 꺼진 뒤 소등합니다.
          </p>
        </PopDialog>
        <PopDialog
          title="야간 센서등"
          :visible="sensorNightModal.visible"
          :okCallback="onSensorNightApply"
          :cancelCallback="sensorNightModal.hide"
          :okText="'적용'"
          :cancelText="'취소'"
          :clickable="false"
        >
          <div class="form-check sensor-ignore-off-check mb-3" style="margin-left: 0">
            <input
              class="form-check-input"
              type="checkbox"
              :id="`sensor-night-enable-draft-${props.data._id}`"
              v-model="nightDraftEnable"
            />
            <label
              class="form-check-label ao-link-label"
              :for="`sensor-night-enable-draft-${props.data._id}`"
            >
              사용
            </label>
          </div>
          <div class="sensor-night-fields">
            <div class="sensor-night-time-picker mb-2">
              <div class="sensor-night-field">
                <label class="form-label mb-1">시작</label>
                <input
                  type="time"
                  class="form-control sensor-night-time-input"
                  v-model="nightDraftStart"
                  :disabled="!nightDraftEnable"
                />
              </div>
              <div class="sensor-night-field">
                <label class="form-label mb-1">종료</label>
                <input
                  type="time"
                  class="form-control sensor-night-time-input"
                  v-model="nightDraftEnd"
                  :disabled="!nightDraftEnable"
                />
              </div>
            </div>
            <div class="sensor-night-field mb-2">
              <label class="form-label mb-1">밝기</label>
              <select
                class="form-select sensor-night-bright-select"
                v-model.number="nightDraftBright"
                :disabled="!nightDraftEnable"
              >
                <option v-for="p in nightBrightOptions" :key="`nb-${p}`" :value="p">
                  {{ p }}%
                </option>
              </select>
            </div>
          </div>
          <p class="sensor-off-timer-hint mb-0">
            연동된 센서등만 해당 시간에 센서 감지 시 지정 밝기로 켜집니다. 「야간 센서 연동」이 비어 있으면 위 센서 연동 채널을 사용합니다. 수동 스위치·앱 ON은 기존 밝기를 유지합니다. 보드 로컬 시각(NTP, 한국) 기준이며 자정 넘김을 허용합니다.
          </p>
        </PopDialog>
        <div class="controller-info-item ao-link-section" v-if="props.showAoLink && peerList.length > 0">
          <div
            v-for="linkMode in aoLinkModes"
            :key="linkMode.key"
            class="ao-link-mode"
          >
            <div class="controller-info-line ao-link-title">
              <i class="fa-solid fa-link me-2"></i>
              <span class="controller-info-label">{{ linkMode.label }}</span>
            </div>
            <div
              v-for="peer in peerList"
              :key="`${linkMode.key}-${peer._id}`"
              class="form-check ao-link-check"
            >
              <input
                class="form-check-input"
                type="checkbox"
                :id="`ao-${linkMode.key}-${props.data._id}-${peer._id}`"
                :checked="isAoLinked(peer._id, linkMode.key)"
                :disabled="aoLinkSaving"
                @change="onAoLinkChange(peer._id, linkMode.key, $event.target.checked)"
              />
              <label
                class="form-check-label ao-link-label"
                :for="`ao-${linkMode.key}-${props.data._id}-${peer._id}`"
              >
                {{ formatControllerSerialWithName(peer) }}
              </label>
            </div>
          </div>
        </div>
      </template>
    </ControllerInfoCard>
  </div>
</template>

<script setup>
import { onMounted, defineProps, computed, getCurrentInstance, ref, reactive } from 'vue';
import { useStore } from 'vuex';
import Axios, { CtrlApi } from '@/api';
import ControllerInfoCard from './ControllerInfoCard.vue';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import {
  resolveControllerDisplayName,
  fromDisplaySensorChannel,
  lampChannelCount,
  formatControllerSerialWithName,
} from '@/common/utils/controllerDisplayName';
import { MQTTCommand } from '@/common/constants';
import useMqttControl from '@/common/hooks/useMqttControl';

const SENSOR_OFF_TIMER_MIN_SEC = 5;
const SENSOR_OFF_TIMER_MAX_SEC = 60 * 60 + 60; // 60분 60초
const SENSOR_NIGHT_DEFAULT_START = '22:00';
const SENSOR_NIGHT_DEFAULT_END = '06:00';
const SENSOR_NIGHT_DEFAULT_BRIGHT = 30;

const clampNightBright = (n) => {
  let v = Math.round(Number(n) || SENSOR_NIGHT_DEFAULT_BRIGHT);
  if (v < 10) v = 10;
  if (v > 100) v = 100;
  v = Math.round(v / 10) * 10;
  if (v < 10) v = 10;
  return v;
};

const minutesToHhMm = (mins) => {
  let m = Number(mins);
  if (!Number.isFinite(m)) m = 0;
  m = ((Math.floor(m) % 1440) + 1440) % 1440;
  const hh = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${hh}:${mm}`;
};

const hhMmToMinutes = (str, fallbackMin) => {
  if (typeof str !== 'string' || !/^\d{2}:\d{2}$/.test(str)) {
    return fallbackMin;
  }
  const [hh, mm] = str.split(':').map((x) => Number(x));
  if (!Number.isInteger(hh) || !Number.isInteger(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
    return fallbackMin;
  }
  return hh * 60 + mm;
};

const { emit, appContext } = getCurrentInstance();
const emitter = appContext.config.globalProperties.emitter;
const mqttControl = reactive(useMqttControl());
const props = defineProps({
  index: {
    type: Number,
    default: 0,
  },
  data: {
    type: Object,
    required: true,
  },
  isSelect: {
    type: Boolean,
    default: false,
  },
  showDelete: {
    type: Boolean,
    default: null, // null이면 isSelect에 따라 자동 결정
  },
  showRooms: {
    type: Boolean,
    default: true,
  },
  /** 일괄스위치 연동 UI 표시 (BLE 조명제어기 설정 화면) */
  showAoLink: {
    type: Boolean,
    default: false,
  },
  /** 같은 zone의 다른 조명제어기 목록 */
  peerControllers: {
    type: Array,
    default: () => [],
  },
});
const store = useStore();
const aoLinkSaving = ref(false);
const aoLinkModes = [
  { key: 'on', label: '일괄스위치 켜기 연동' },
  { key: 'off', label: '일괄스위치 끄기 연동' },
];
const sensorLinkSaving = ref(false);
const sensorIgnoreOffSaving = ref(false);
const aoSkipSensorOnSaving = ref(false);
const sensorOffGuardSaving = ref(false);
const sensorOffTimerSaving = ref(false);
const sensorOffTimerModal = reactive(useModal());
const timerDraftMin = ref(0);
const timerDraftSec = ref(0);
const timerMinOptions = Array.from({ length: 61 }, (_, i) => i);
/** 초: 0 + 5초 간격 (5…60) */
const timerSecOptions = [0, ...Array.from({ length: 12 }, (_, i) => (i + 1) * 5)];
const sensorNightSaving = ref(false);
const sensorNightLinkSaving = ref(false);
const sensorNightModal = reactive(useModal());
const nightDraftEnable = ref(false);
const nightDraftStart = ref(SENSOR_NIGHT_DEFAULT_START);
const nightDraftEnd = ref(SENSOR_NIGHT_DEFAULT_END);
const nightDraftBright = ref(SENSOR_NIGHT_DEFAULT_BRIGHT);
const nightBrightOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * 10);
const sensorChannels = computed(() => {
  const n = lampChannelCount(props.data);
  return Array.from({ length: n }, (_, i) => i + 1);
});

/** 미설정(undefined)이면 기본 선택(true) */
const isAoSkipSensorOn = computed(() => props.data?.aoSkipSensorOn !== false);

/** 미설정(undefined)이면 기본 선택(true) — 수동 소등 - 5초 센서 지연 */
const isSensorOffGuard = computed(() => props.data?.sensorOffGuard !== false);

const sensorOffTimerSec = computed(() => {
  const n = Number(props.data?.sensorOffTimerSec);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(SENSOR_OFF_TIMER_MAX_SEC, Math.floor(n));
});

const sensorOffTimerDisplay = computed(() => {
  const total = sensorOffTimerSec.value;
  if (total <= 0) return '계속 켜짐';
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}초`;
  if (s === 0) return `${m}분`;
  return `${m}분 ${s}초`;
});

const isSensorNightEnable = computed(() => !!props.data?.sensorNightEnable);

const sensorNightStartMin = computed(() => {
  const n = Number(props.data?.sensorNightStartMin);
  if (!Number.isFinite(n)) return hhMmToMinutes(SENSOR_NIGHT_DEFAULT_START, 22 * 60);
  return ((Math.floor(n) % 1440) + 1440) % 1440;
});

const sensorNightEndMin = computed(() => {
  const n = Number(props.data?.sensorNightEndMin);
  if (!Number.isFinite(n)) return hhMmToMinutes(SENSOR_NIGHT_DEFAULT_END, 6 * 60);
  return ((Math.floor(n) % 1440) + 1440) % 1440;
});

const sensorNightBright = computed(() =>
  clampNightBright(props.data?.sensorNightBright ?? SENSOR_NIGHT_DEFAULT_BRIGHT),
);

const sensorNightDisplay = computed(() => {
  if (!isSensorNightEnable.value) return '없음';
  return `${minutesToHhMm(sensorNightStartMin.value)}~${minutesToHhMm(sensorNightEndMin.value)} · ${sensorNightBright.value}%`;
});

const zone = computed(() => store.getters['home/getZones']?.find(
  (zone) => zone.controllers.find((controller) => controller._id === props.data._id)
) || undefined);
const groups = computed(() => zone.value?.groups.filter(group => group.devices.some(device => device.controllerId == props.data._id)) || []);

const hasSsid = computed(() => {
  const ssid = props.data?.ssid;
  return typeof ssid === 'string' && ssid.length > 0;
});

const peerList = computed(() =>
  (props.peerControllers || []).filter(
    (c) => c?._id && c._id !== props.data?._id,
  ),
);

const linkedSet = (mode) => computed(() => {
  const field = mode === 'on' ? 'aoOnLinkControllers' : 'aoOffLinkControllers';
  const list = Array.isArray(props.data?.[field])
    ? props.data[field]
    : Array.isArray(props.data?.aoLinkControllers)
      ? props.data.aoLinkControllers
      : [];
  return new Set(list);
});

const isAoLinked = (serial, mode) => linkedSet(mode).value.has(serial);

/**
 * 정책: 센서 전체 해제는 허용되어야 한다.
 * - 미설정(null/undefined)만 모델 기본값(M24/M22=[1], M02=[12]).
 * - 명시적 [] 는 연동 없음. 「최소 1개 유지」로 [1]을 다시 넣지 말 것.
 */
const normalizeSensorChannels = (list, serial) => {
  const maxCh = lampChannelCount(serial);
  const fallback =
    typeof serial === 'string' && serial.startsWith('M02_') ? [12] : [1];
  // 미설정만 기본값. 명시적 [] 는 전 채널 해제.
  if (!Array.isArray(list)) return fallback;
  const set = new Set();
  for (const n of list) {
    const ch = Number(n);
    if (Number.isInteger(ch) && ch >= 1 && ch <= maxCh) set.add(ch);
  }
  const out = [...set].sort((a, b) => a - b);
  if (out.length === 0) return [];
  // M02: 스키마 기본 단독 [1] → 10217 CH12(실크 1)
  if (
    typeof serial === 'string' &&
    serial.startsWith('M02_') &&
    out.length === 1 &&
    out[0] === 1
  ) {
    return [12];
  }
  return out;
};

/** 야간 센서 연동: 미설정·[] = 비움(야간에 기본 센서 연동 사용). M02 [1]→[12] 보정. */
const normalizeNightSensorChannels = (list, serial) => {
  const maxCh = lampChannelCount(serial);
  if (!Array.isArray(list)) return [];
  const set = new Set();
  for (const n of list) {
    const ch = Number(n);
    if (Number.isInteger(ch) && ch >= 1 && ch <= maxCh) set.add(ch);
  }
  const out = [...set].sort((a, b) => a - b);
  if (out.length === 0) return [];
  if (
    typeof serial === 'string' &&
    serial.startsWith('M02_') &&
    out.length === 1 &&
    out[0] === 1
  ) {
    return [12];
  }
  return out;
};

const sensorLinkedSet = computed(() =>
  new Set(normalizeSensorChannels(props.data?.sensorLinkChannels, props.data?._id)),
);

const sensorNightLinkedSet = computed(() =>
  new Set(
    normalizeNightSensorChannels(props.data?.sensorNightLinkChannels, props.data?._id),
  ),
);

/** UI N번 = 표시(실크) 번호. M02는 펌웨어 채널과 역순. */
const isSensorLinked = (displayCh) => {
  const fw = fromDisplaySensorChannel(displayCh, props.data);
  return sensorLinkedSet.value.has(fw);
};

const isSensorNightLinked = (displayCh) => {
  const fw = fromDisplaySensorChannel(displayCh, props.data);
  return sensorNightLinkedSet.value.has(fw);
};

const requestUserInfoPush = () => {
  if (!props.data?._id) return;
  mqttControl.onPublishController(props.data._id, {
    serial: props.data._id,
    cmd: MQTTCommand.UserInfoRefresh,
  });
};

const onSensorLinkChange = async (displayCh, checked) => {
  if (!props.data?._id || sensorLinkSaving.value) return;
  const fw = fromDisplaySensorChannel(displayCh, props.data);
  const next = new Set(normalizeSensorChannels(props.data?.sensorLinkChannels, props.data?._id));
  if (checked) next.add(fw);
  else next.delete(fw);
  // 마지막 채널 해제 → [] 저장 허용 (전체 해제 정책)
  const sensorLinkChannels = [...next].sort((a, b) => a - b);

  sensorLinkSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { sensorLinkChannels },
    );
    if (result?.resultCode === '0000') {
      emit('onSensorLinkUpdated', {
        id: props.data._id,
        sensorLinkChannels:
          result.resultData?.sensorLinkChannels ?? sensorLinkChannels,
      });
      requestUserInfoPush();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '센서 연동 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onSensorLinkChange failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '센서 연동 저장에 실패했습니다.',
    });
  } finally {
    sensorLinkSaving.value = false;
  }
};

const onSensorNightLinkChange = async (displayCh, checked) => {
  if (!props.data?._id || sensorNightLinkSaving.value || !isSensorNightEnable.value) return;
  const fw = fromDisplaySensorChannel(displayCh, props.data);
  const next = new Set(
    normalizeNightSensorChannels(props.data?.sensorNightLinkChannels, props.data?._id),
  );
  if (checked) next.add(fw);
  else next.delete(fw);
  const sensorNightLinkChannels = [...next].sort((a, b) => a - b);

  sensorNightLinkSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { sensorNightLinkChannels },
    );
    if (result?.resultCode === '0000') {
      emit('onSensorNightLinkUpdated', {
        id: props.data._id,
        sensorNightLinkChannels:
          result.resultData?.sensorNightLinkChannels ?? sensorNightLinkChannels,
      });
      requestUserInfoPush();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '야간 센서 연동 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onSensorNightLinkChange failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '야간 센서 연동 저장에 실패했습니다.',
    });
  } finally {
    sensorNightLinkSaving.value = false;
  }
};

const onSensorIgnoreOffChange = async (checked) => {
  if (!props.data?._id || sensorIgnoreOffSaving.value) return;
  const sensorIgnoreOff = !!checked;

  sensorIgnoreOffSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { sensorIgnoreOff },
    );
    if (result?.resultCode === '0000') {
      emit('onSensorIgnoreOffUpdated', {
        id: props.data._id,
        sensorIgnoreOff:
          result.resultData?.sensorIgnoreOff !== undefined
            ? !!result.resultData.sensorIgnoreOff
            : sensorIgnoreOff,
      });
      requestUserInfoPush();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '센서 옵션 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onSensorIgnoreOffChange failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '센서 옵션 저장에 실패했습니다.',
    });
  } finally {
    sensorIgnoreOffSaving.value = false;
  }
};

const onSensorOffTimerRowClick = () => {
  if (!props.data?.sensorIgnoreOff || sensorOffTimerSaving.value) return;
  const total = sensorOffTimerSec.value;
  // 미설정(0): 분=0, 초=0. 기존 초는 5초 단위로 맞춤.
  timerDraftMin.value = Math.floor(total / 60);
  let sec = total % 60;
  if (sec !== 0) {
    sec = Math.min(60, Math.round(sec / 5) * 5);
    if (sec === 0) sec = 5;
  }
  timerDraftSec.value = sec;
  sensorOffTimerModal.show();
};

const onSensorOffTimerApply = async () => {
  if (!props.data?._id || sensorOffTimerSaving.value) return;
  const min = Math.max(0, Math.min(60, Number(timerDraftMin.value) || 0));
  const sec = Math.max(0, Math.min(60, Number(timerDraftSec.value) || 0));
  let total = min * 60 + sec;
  if (total > SENSOR_OFF_TIMER_MAX_SEC) total = SENSOR_OFF_TIMER_MAX_SEC;
  if (total > 0 && total < SENSOR_OFF_TIMER_MIN_SEC) {
    emitter?.emit?.('showPopAlert', {
      description: `타이머는 최소 ${SENSOR_OFF_TIMER_MIN_SEC}초 이상이어야 합니다.`,
    });
    return;
  }

  sensorOffTimerSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { sensorOffTimerSec: total },
    );
    if (result?.resultCode === '0000') {
      const saved =
        result.resultData?.sensorOffTimerSec !== undefined
          ? Number(result.resultData.sensorOffTimerSec) || 0
          : total;
      emit('onSensorOffTimerUpdated', {
        id: props.data._id,
        sensorOffTimerSec: saved,
      });
      requestUserInfoPush();
      sensorOffTimerModal.hide();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '타이머 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onSensorOffTimerApply failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '타이머 저장에 실패했습니다.',
    });
  } finally {
    sensorOffTimerSaving.value = false;
  }
};

const onSensorNightRowClick = () => {
  if (sensorNightSaving.value) return;
  nightDraftEnable.value = isSensorNightEnable.value;
  nightDraftStart.value = minutesToHhMm(sensorNightStartMin.value);
  nightDraftEnd.value = minutesToHhMm(sensorNightEndMin.value);
  nightDraftBright.value = sensorNightBright.value;
  sensorNightModal.show();
};

const onSensorNightApply = async () => {
  if (!props.data?._id || sensorNightSaving.value) return;
  const sensorNightEnable = !!nightDraftEnable.value;
  const sensorNightStartMin = hhMmToMinutes(
    nightDraftStart.value,
    hhMmToMinutes(SENSOR_NIGHT_DEFAULT_START, 22 * 60),
  );
  const sensorNightEndMin = hhMmToMinutes(
    nightDraftEnd.value,
    hhMmToMinutes(SENSOR_NIGHT_DEFAULT_END, 6 * 60),
  );
  const sensorNightBright = clampNightBright(nightDraftBright.value);

  sensorNightSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      {
        sensorNightEnable,
        sensorNightStartMin,
        sensorNightEndMin,
        sensorNightBright,
      },
    );
    if (result?.resultCode === '0000') {
      const rd = result.resultData || {};
      emit('onSensorNightUpdated', {
        id: props.data._id,
        sensorNightEnable:
          rd.sensorNightEnable !== undefined ? !!rd.sensorNightEnable : sensorNightEnable,
        sensorNightStartMin:
          rd.sensorNightStartMin !== undefined
            ? Number(rd.sensorNightStartMin)
            : sensorNightStartMin,
        sensorNightEndMin:
          rd.sensorNightEndMin !== undefined
            ? Number(rd.sensorNightEndMin)
            : sensorNightEndMin,
        sensorNightBright:
          rd.sensorNightBright !== undefined
            ? clampNightBright(rd.sensorNightBright)
            : sensorNightBright,
      });
      requestUserInfoPush();
      sensorNightModal.hide();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '야간 밝기 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onSensorNightApply failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '야간 밝기 저장에 실패했습니다.',
    });
  } finally {
    sensorNightSaving.value = false;
  }
};

const onAoSkipSensorOnChange = async (checked) => {
  if (!props.data?._id || aoSkipSensorOnSaving.value) return;
  const aoSkipSensorOn = !!checked;

  aoSkipSensorOnSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { aoSkipSensorOn },
    );
    if (result?.resultCode === '0000') {
      emit('onAoSkipSensorOnUpdated', {
        id: props.data._id,
        aoSkipSensorOn:
          result.resultData?.aoSkipSensorOn !== undefined
            ? !!result.resultData.aoSkipSensorOn
            : aoSkipSensorOn,
      });
      requestUserInfoPush();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '센서 옵션 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onAoSkipSensorOnChange failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '센서 옵션 저장에 실패했습니다.',
    });
  } finally {
    aoSkipSensorOnSaving.value = false;
  }
};

const onSensorOffGuardChange = async (checked) => {
  if (!props.data?._id || sensorOffGuardSaving.value) return;
  const sensorOffGuard = !!checked;

  sensorOffGuardSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { sensorOffGuard },
    );
    if (result?.resultCode === '0000') {
      emit('onSensorOffGuardUpdated', {
        id: props.data._id,
        sensorOffGuard:
          result.resultData?.sensorOffGuard !== undefined
            ? !!result.resultData.sensorOffGuard
            : sensorOffGuard,
      });
      requestUserInfoPush();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '센서 옵션 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onSensorOffGuardChange failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '센서 옵션 저장에 실패했습니다.',
    });
  } finally {
    sensorOffGuardSaving.value = false;
  }
};

const onAoLinkChange = async (peerSerial, mode, checked) => {
  if (!props.data?._id || aoLinkSaving.value) return;
  const field = mode === 'on' ? 'aoOnLinkControllers' : 'aoOffLinkControllers';
  const current = Array.isArray(props.data?.[field])
    ? props.data[field]
    : Array.isArray(props.data?.aoLinkControllers)
      ? props.data.aoLinkControllers
      : [];
  const next = new Set(current);
  if (checked) next.add(peerSerial);
  else next.delete(peerSerial);
  const links = [...next].filter((s) =>
    peerList.value.some((p) => p._id === s),
  );

  aoLinkSaving.value = true;
  try {
    const result = await Axios.patch(
      CtrlApi.UPDATE_CTRL.replace(':id', props.data._id),
      { [field === 'aoOnLinkControllers' ? 'aoOnLinkControllers' : 'aoOffLinkControllers']: links },
    );
    if (result?.resultCode === '0000') {
      emit('onAoLinkUpdated', {
        id: props.data._id,
        [field]: result.resultData?.[field] ?? links,
      });
      requestUserInfoPush();
    } else {
      emitter?.emit?.('showPopAlert', {
        description: result?.resultMessage || '일괄스위치 연동 저장에 실패했습니다.',
      });
    }
  } catch (e) {
    console.error('onAoLinkChange failed:', e);
    emitter?.emit?.('showPopAlert', {
      description: '일괄스위치 연동 저장에 실패했습니다.',
    });
  } finally {
    aoLinkSaving.value = false;
  }
};

const controllerWithLastVersion = computed(() => {
  const base = props.data || {};
  if (!base?.model) return base;
  const products = store.getters['home/getProducts'] || [];
  const product = products.find((p) => p.model === base.model);
  const lastVersion = product?.version;
  return lastVersion !== undefined ? { ...base, lastVersion } : base;
});

const controllerTypeText = computed(() => {
  if (props.data?.model || props.data?.hwRevision || props.data?._id) {
    const products = store.getters['home/getProducts'] || [];
    return resolveControllerDisplayName(props.data, products);
  }
  return '컨트롤러';
});

onMounted(() => {
  console.log('ControllerItem.onMounted().props =>', props);
});

const onDeleteClick = () => {
  console.log('ControllerItem.onDeleteClick =>', { id: props.data._id });
  emit('onDeleteClick', { id: props.data._id });
};

const onCheckboxChange = (e) => {
  for (const item of document.getElementsByClassName('check-controller')) {
    item.checked = false;
  }
  e.target.checked = true;
  emit('onChecked', e.target.value);
};

// 삭제 버튼 표시 여부 결정
const shouldShowDelete = computed(() => {
  if (props.showDelete !== null) {
    return props.showDelete;
  }
  // isSelect가 true이면 삭제 버튼 숨김
  return !props.isSelect;
});

// 방 목록 표시 여부 결정
const shouldShowRooms = computed(() => {
  return props.showRooms;
});

/** 펌웨어 업데이트 카드와 동일: 헤더 아래 방 이름 태그 */
const roomDisplayNames = computed(() => {
  if (!shouldShowRooms.value) return [];
  return (groups.value || [])
    .map((g) => g?.name)
    .filter((n) => typeof n === 'string' && n.length > 0);
});
</script>

<style scoped>
.controller-delete-btn {
  cursor: pointer;
  padding: 0.5rem;
  color: #6c757d;
  transition: all 0.2s ease;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
}

.controller-delete-btn:hover {
  background-color: #f8f9fa;
  color: #212529;
}

.controller-info-value span {
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
  vertical-align: baseline;
}

.controller-info-no-data {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
  font-style: italic;
}

.ao-link-section,
.sensor-link-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.ao-link-title {
  width: 100%;
  margin-bottom: 0.15rem;
}

.ao-link-check {
  margin-left: 1.5rem;
  margin-bottom: 0.15rem;
}

.sensor-link-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin-left: 1.5rem;
  gap: 0.35rem 0.5rem;
  width: calc(100% - 1.5rem);
}

.sensor-link-section > .ao-link-title + .sensor-link-grid {
  margin-bottom: 0.35rem;
}

.sensor-link-grid.disabled {
  opacity: 0.45;
  pointer-events: none;
}

.night-sensor-link-title {
  margin-left: 1.5rem;
  margin-top: 1rem;
  display: flex;
  align-items: baseline;
  width: calc(100% - 1.5rem);
}

.night-sensor-link-title.disabled {
  opacity: 0.45;
}

.sensor-link-check {
  margin-bottom: 0;
  min-width: 0;
  white-space: nowrap;
}

.sensor-ignore-off-check {
  margin-left: 1.5rem;
  margin-top: 0.25rem;
  margin-bottom: 0.15rem;
}

.sensor-timer-mode-group {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  width: 100%;
}

.sensor-section-divider {
  margin-left: 1.5rem;
  margin-top: 0.55rem;
  margin-bottom: 0.45rem;
  width: calc(100% - 1.5rem);
  height: 1px;
  background: #dee2e6;
}

.sensor-off-timer-row {
  margin-left: 1.5rem;
  margin-top: 0.15rem;
  margin-bottom: 0.15rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: calc(100% - 1.5rem);
  min-height: 2.5rem;
  padding: 0.55rem 0.65rem;
  border-radius: 0.4rem;
  cursor: pointer;
  background: #f8f9fa;
}

.sensor-off-timer-row.disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.sensor-off-timer-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}

.sensor-off-timer-picker {
  display: flex;
  gap: 0.75rem;
}

.sensor-off-timer-field {
  flex: 1;
  min-width: 0;
}

.sensor-off-timer-field .form-select {
  min-height: 2.5rem;
  height: 2.5rem;
}

.sensor-night-fields {
  width: 100%;
  box-sizing: border-box;
}

.sensor-night-field {
  position: relative;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.sensor-night-time-picker {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
}

.sensor-night-time-input,
.sensor-night-bright-select {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 2.75rem;
  min-height: 2.75rem;
}

.sensor-night-time-input {
  padding: 0 0.5rem;
  text-align: center;
  font-size: 1rem;
  line-height: 1.25;
  display: block;
  -webkit-appearance: none;
  appearance: none;
}

/* iOS/WebKit: 시간 값이 칸 상단에 붙는 문제 보정 */
.sensor-night-time-input::-webkit-date-and-time-value {
  text-align: center;
  margin: 0 auto;
  min-height: 2.5rem;
  line-height: 2.5rem;
}

.sensor-night-time-input::-webkit-datetime-edit {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
}

.sensor-night-time-input::-webkit-datetime-edit-fields-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sensor-night-time-input::-webkit-calendar-picker-indicator {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: pointer;
}

.sensor-night-bright-select {
  padding-top: 0;
  padding-bottom: 0;
}

.sensor-off-timer-hint {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.ao-link-label {
  font-size: 0.85rem;
  color: #495057;
  word-break: break-all;
}
</style>
